import { Component, OnInit, HostListener, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { GoogleService } from 'src/app/services/auth/google/google.service';
import { LinkedinService } from 'src/app/services/auth/linkedin/linkedin.service';
import { UserService } from 'src/app/services/user/user.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.css'],
})
export class SignUpComponent implements OnInit {
  userForm!: FormGroup;
  companyForm!: FormGroup;

  /** Active tab: 'user' | 'company' */
  activeTab: 'user' | 'company' = 'user';

  showPassword = false;
  showConfirmPassword = false;
  submitted = false;
  isLoading = false;

  // ─── File Inputs ───
  taxRegistrationFile: File | null = null;
  commercialRegistrationFile: File | null = null;

  // ─── Country Dropdown ───
  countries: any[] = [
    // Arab Countries
    { name: 'Algeria', code: 'dz', dial: '+213', flag: 'https://flagcdn.com/w20/dz.png' },
    { name: 'Bahrain', code: 'bh', dial: '+973', flag: 'https://flagcdn.com/w20/bh.png' },
    { name: 'Comoros', code: 'km', dial: '+269', flag: 'https://flagcdn.com/w20/km.png' },
    { name: 'Djibouti', code: 'dj', dial: '+253', flag: 'https://flagcdn.com/w20/dj.png' },
    { name: 'Egypt', code: 'eg', dial: '+20', flag: 'https://flagcdn.com/w20/eg.png' },
    { name: 'Iraq', code: 'iq', dial: '+964', flag: 'https://flagcdn.com/w20/iq.png' },
    { name: 'Jordan', code: 'jo', dial: '+962', flag: 'https://flagcdn.com/w20/jo.png' },
    { name: 'Kuwait', code: 'kw', dial: '+965', flag: 'https://flagcdn.com/w20/kw.png' },
    { name: 'Lebanon', code: 'lb', dial: '+961', flag: 'https://flagcdn.com/w20/lb.png' },
    { name: 'Libya', code: 'ly', dial: '+218', flag: 'https://flagcdn.com/w20/ly.png' },
    { name: 'Mauritania', code: 'mr', dial: '+222', flag: 'https://flagcdn.com/w20/mr.png' },
    { name: 'Morocco', code: 'ma', dial: '+212', flag: 'https://flagcdn.com/w20/ma.png' },
    { name: 'Oman', code: 'om', dial: '+968', flag: 'https://flagcdn.com/w20/om.png' },
    { name: 'Palestine', code: 'ps', dial: '+970', flag: 'https://flagcdn.com/w20/ps.png' },
    { name: 'Qatar', code: 'qa', dial: '+974', flag: 'https://flagcdn.com/w20/qa.png' },
    { name: 'Saudi Arabia', code: 'sa', dial: '+966', flag: 'https://flagcdn.com/w20/sa.png' },
    { name: 'Somalia', code: 'so', dial: '+252', flag: 'https://flagcdn.com/w20/so.png' },
    { name: 'Sudan', code: 'sd', dial: '+249', flag: 'https://flagcdn.com/w20/sd.png' },
    { name: 'Syria', code: 'sy', dial: '+963', flag: 'https://flagcdn.com/w20/sy.png' },
    { name: 'Tunisia', code: 'tn', dial: '+216', flag: 'https://flagcdn.com/w20/tn.png' },
    { name: 'United Arab Emirates', code: 'ae', dial: '+971', flag: 'https://flagcdn.com/w20/ae.png' },
    { name: 'Yemen', code: 'ye', dial: '+967', flag: 'https://flagcdn.com/w20/ye.png' },
    // EU Countries
    { name: 'Austria', code: 'at', dial: '+43', flag: 'https://flagcdn.com/w20/at.png' },
    { name: 'Belgium', code: 'be', dial: '+32', flag: 'https://flagcdn.com/w20/be.png' },
    { name: 'Bulgaria', code: 'bg', dial: '+359', flag: 'https://flagcdn.com/w20/bg.png' },
    { name: 'Croatia', code: 'hr', dial: '+385', flag: 'https://flagcdn.com/w20/hr.png' },
    { name: 'Cyprus', code: 'cy', dial: '+357', flag: 'https://flagcdn.com/w20/cy.png' },
    { name: 'Czech Republic', code: 'cz', dial: '+420', flag: 'https://flagcdn.com/w20/cz.png' },
    { name: 'Denmark', code: 'dk', dial: '+45', flag: 'https://flagcdn.com/w20/dk.png' },
    { name: 'Estonia', code: 'ee', dial: '+372', flag: 'https://flagcdn.com/w20/ee.png' },
    { name: 'Finland', code: 'fi', dial: '+358', flag: 'https://flagcdn.com/w20/fi.png' },
    { name: 'France', code: 'fr', dial: '+33', flag: 'https://flagcdn.com/w20/fr.png' },
    { name: 'Germany', code: 'de', dial: '+49', flag: 'https://flagcdn.com/w20/de.png' },
    { name: 'Greece', code: 'gr', dial: '+30', flag: 'https://flagcdn.com/w20/gr.png' },
    { name: 'Hungary', code: 'hu', dial: '+36', flag: 'https://flagcdn.com/w20/hu.png' },
    { name: 'Ireland', code: 'ie', dial: '+353', flag: 'https://flagcdn.com/w20/ie.png' },
    { name: 'Italy', code: 'it', dial: '+39', flag: 'https://flagcdn.com/w20/it.png' },
    { name: 'Latvia', code: 'lv', dial: '+371', flag: 'https://flagcdn.com/w20/lv.png' },
    { name: 'Lithuania', code: 'lt', dial: '+370', flag: 'https://flagcdn.com/w20/lt.png' },
    { name: 'Luxembourg', code: 'lu', dial: '+352', flag: 'https://flagcdn.com/w20/lu.png' },
    { name: 'Malta', code: 'mt', dial: '+356', flag: 'https://flagcdn.com/w20/mt.png' },
    { name: 'Netherlands', code: 'nl', dial: '+31', flag: 'https://flagcdn.com/w20/nl.png' },
    { name: 'Poland', code: 'pl', dial: '+48', flag: 'https://flagcdn.com/w20/pl.png' },
    { name: 'Portugal', code: 'pt', dial: '+351', flag: 'https://flagcdn.com/w20/pt.png' },
    { name: 'Romania', code: 'ro', dial: '+40', flag: 'https://flagcdn.com/w20/ro.png' },
    { name: 'Slovakia', code: 'sk', dial: '+421', flag: 'https://flagcdn.com/w20/sk.png' },
    { name: 'Slovenia', code: 'si', dial: '+386', flag: 'https://flagcdn.com/w20/si.png' },
    { name: 'Spain', code: 'es', dial: '+34', flag: 'https://flagcdn.com/w20/es.png' },
    { name: 'Sweden', code: 'se', dial: '+46', flag: 'https://flagcdn.com/w20/se.png' }
  ];

  selectedCountry = this.countries[0];
  showCountryDropdown = false;
  countrySearch = '';

  @ViewChild('countryTrigger') countryTrigger!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private linkedIn: LinkedinService,
    private google: GoogleService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.initUserForm();
    this.initCompanyForm();
    // Read ?type=user|company from URL set by landing page
    this.route.queryParams.subscribe((params) => {
      const type = params['type'];
      if (type === 'company') {
        this.activeTab = 'company';
      } else {
        this.activeTab = 'user';
      }
    });
  }

  // ─── Forms ───
  initUserForm() {
    this.userForm = this.fb.group(
      {
        name: ['', [Validators.required, Validators.minLength(2), Validators.pattern(/^[a-zA-Z\u0600-\u06FF\s]+$/)]],
        phoneNumber: ['', [Validators.pattern(/^[0-9+\-\s\(\)]+$/)]],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(8),
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  initCompanyForm() {
    this.companyForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/)]],
      confirmPassword: ['', [Validators.required]],
      phoneNumber: ['', [Validators.pattern(/^[0-9+\-\s\(\)]+$/)]],
      ownerName: ['', [Validators.required, Validators.minLength(2)]],
      responsiblePersons: [''],
      companySizeFrom: ['', [Validators.required, Validators.min(1)]],
      companySizeTo: ['', [Validators.required, Validators.min(1)]],
    },
    { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else if (confirmPassword?.hasError('passwordMismatch')) {
      const errors = { ...confirmPassword.errors };
      delete errors['passwordMismatch'];
      confirmPassword.setErrors(Object.keys(errors).length ? errors : null);
    }
    return null;
  }

  // ─── Tab Switching ───
  switchTab(tab: 'user' | 'company') {
    this.activeTab = tab;
    this.submitted = false;
    this.isLoading = false;
  }

  get isCompany() { return this.activeTab === 'company'; }

  // ─── Active form helpers ───
  get activeForm(): FormGroup {
    return this.isCompany ? this.companyForm : this.userForm;
  }

  hasError(controlName: string): boolean {
    const control = this.activeForm.get(controlName);
    return !!(control?.errors && (control.touched || this.submitted));
  }

  isValid(controlName: string): boolean {
    const control = this.activeForm.get(controlName);
    return !!(control?.valid && control.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.activeForm.get(controlName);
    if (control?.errors && (control.touched || this.submitted)) {
      if (control.errors['required']) return this.translate.instant('validation.required');
      switch (controlName) {
        case 'name':
          if (control.errors['minlength']) return this.translate.instant('validation.name_min_length');
          if (control.errors['pattern'])   return this.translate.instant('validation.name_pattern');
          break;
        case 'phoneNumber':
          if (control.errors['pattern'])   return this.translate.instant('validation.phone_pattern');
          break;
        case 'email':
          if (control.errors['email'])     return this.translate.instant('validation.email_invalid');
          break;
        case 'password':
          if (control.errors['minlength']) return this.translate.instant('validation.password_min_length');
          if (control.errors['pattern'])   return this.translate.instant('validation.password_pattern');
          break;
        case 'confirmPassword':
          if (control.errors['passwordMismatch']) return this.translate.instant('validation.password_mismatch');
          break;
        case 'companySizeFrom':
        case 'companySizeTo':
          if (control.errors['min']) return this.translate.instant('validation.required');
          break;
      }
    }
    return '';
  }

  // ─── Sweet Alert Error Helper ───
  private showErrorAlert(message: string) {
    Swal.fire({
      icon: 'error',
      title: this.translate.instant('Error') || 'Error',
      text: message,
      confirmButtonText: this.translate.instant('OK') || 'OK',
      customClass: {
        confirmButton: 'swal-confirm-btn',
      },
    });
  }

  // ─── File Handlers ───
  onTaxRegistrationChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.taxRegistrationFile = input.files?.[0] ?? null;
  }

  onCommercialRegistrationChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.commercialRegistrationFile = input.files?.[0] ?? null;
  }

  // ─── Country Dropdown ───
  get filteredCountries() {
    const q = this.countrySearch.toLowerCase().trim();
    if (!q) return this.countries;
    return this.countries.filter(c =>
      c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.includes(q)
    );
  }

  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
    if (this.showCountryDropdown) this.countrySearch = '';
  }

  selectCountry(country: any): void {
    this.selectedCountry = country;
    this.showCountryDropdown = false;
    this.countrySearch = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const trigger = this.countryTrigger?.nativeElement;
    if (trigger && !trigger.contains(event.target)) {
      this.showCountryDropdown = false;
    }
  }

  // ─── Password Visibility ───
  toggleShowPassword() { this.showPassword = !this.showPassword; }
  toggleConfirmShowPassword() { this.showConfirmPassword = !this.showConfirmPassword; }

  // ─── Submit ───
  signUp() {
    this.submitted = true;
    this.markFormGroupTouched();

    if (this.activeForm.invalid) return;

    this.isLoading = true;

    if (this.isCompany) {
      this.registerCompany();
    } else {
      this.registerUser();
    }
  }

private registerUser() {
  const v = this.userForm.value;

  const userAuth = {
    email: v.email,
    password: v.password,
    name: v.name,
  };

  const userData = {
    name: v.name,
    email: v.email,
    phoneNumber: this.selectedCountry.dial + v.phoneNumber,
    country: this.selectedCountry.name,
    picture: null,
  };

  this.authService.registerUser(userAuth).subscribe({
    next: (response: any) => {
      this.isLoading = false;
      this.userService.setTokenInfo(response.accessToken, response.refreshToken, response.userId, response.role);
      this.userService.updateUserDetails(userData).subscribe({
        next: (res: any) => {
          const userInfo = res
            ? { ...JSON.parse(res), picture: null, role: response.role }
            : { ...userData, role: response.role };
          this.userService.setUserInfo(userInfo);

          const redirectUrl = localStorage.getItem('redirectUrl');
          localStorage.removeItem('redirectUrl');

          const roleRaw = userInfo.role;
          const roleName = typeof roleRaw === 'object' && roleRaw !== null
            ? roleRaw.name
            : roleRaw;
          const isCompany = roleName?.toString().toUpperCase() === 'COMPANY';

          if (redirectUrl && redirectUrl !== '/') {
            this.router.navigateByUrl(redirectUrl);
          } else if (isCompany) {
            this.router.navigate(['/company-home']);
          } else {
            this.router.navigate(['/home']);
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          const message = err?.error?.message || 'Something went wrong.';
          this.showErrorAlert(message);
        },
      });
    },
    error: (err: any) => {
      this.isLoading = false;
      this.submitted = false;
      const message = err?.error?.message || 'Something went wrong.';
      this.showErrorAlert(message);
    },
  });
}

private registerCompany() {
  const v = this.companyForm.value;

  const requestData = {
    name: v.name,
    email: v.email,
    password: v.password,
    phoneNumber: this.selectedCountry.dial + (v.phoneNumber || ''),
    ownerName: v.ownerName,
    responsiblePersons: v.responsiblePersons
      ? v.responsiblePersons.split(',').map((s: string) => s.trim()).filter(Boolean)
      : [],
    companySizeFrom: +v.companySizeFrom,
    companySizeTo: +v.companySizeTo,
  };

  this.authService.registerCompany(
    requestData,
    this.taxRegistrationFile,
    this.commercialRegistrationFile
  ).subscribe({
    next: (response: any) => {
      this.isLoading = false;
      this.userService.setTokenInfo(response.accessToken, response.refreshToken, response.userId, response.role);
      const userInfo = { name: v.name, email: v.email, picture: null, role: response.role };
      this.userService.setUserInfo(userInfo);

      const redirectUrl = localStorage.getItem('redirectUrl');
      localStorage.removeItem('redirectUrl');

      const roleRaw = userInfo.role;
      const roleName = typeof roleRaw === 'object' && roleRaw !== null
        ? roleRaw.name
        : roleRaw;
      const isCompany = roleName?.toString().toUpperCase() === 'COMPANY';

      if (redirectUrl && redirectUrl !== '/') {
        this.router.navigateByUrl(redirectUrl);
      } else if (isCompany) {
        this.router.navigate(['/company-home']);
      } else {
        this.router.navigate(['/home']);
      }
    },
    error: (err: any) => {
      this.isLoading = false;
      this.submitted = false;
      const message = err?.error?.message || 'Something went wrong.';
      this.showErrorAlert(message);
    },
  });
}

  private markFormGroupTouched() {
    Object.keys(this.activeForm.controls).forEach((key) => {
      this.activeForm.get(key)?.markAsTouched();
    });
  }

  registerWithLinkedIn() { this.linkedIn.loginWithLinkedIn(); }
  registerWithGoogle() { this.google.login(); }
}
